import { useState } from 'react';
import type { Note, NoteCategory, CreateNoteInput } from '../types/note';
import ImageUploader from './ImageUploader';
import RichTextEditor from './RichTextEditor';
import { textToHtml, isHtml } from '../utils/contentFormat';

interface NoteEditorProps {
  note?: Note;
  onSave: (note: CreateNoteInput & { images?: string[] }) => Promise<void>;
  onCancel: () => void;
}

const categories: { value: NoteCategory; label: string }[] = [
  { value: 'idea', label: 'Idea' },
  { value: 'expense', label: 'Expense' },
  { value: 'important', label: 'Important' },
  { value: 'private', label: 'Private' },
  { value: 'reminder', label: 'Reminder' },
  { value: 'bookmark', label: 'Bookmark' },
];

export default function NoteEditor({ note, onSave, onCancel }: NoteEditorProps) {
  const [title, setTitle] = useState(note?.title || '');
  // Convert plain text to HTML if needed
  const initialContent = note?.content ? (isHtml(note.content) ? note.content : textToHtml(note.content)) : '';
  const [content, setContent] = useState(initialContent);
  const [category, setCategory] = useState<NoteCategory>(note?.category || 'idea');
  const [images, setImages] = useState<string[]>(note?.images || []);
  const [isSaving, setIsSaving] = useState(false);
  const [showImageUploader, setShowImageUploader] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      await onSave({
        title,
        content,
        category,
        isEncrypted: category === 'private',
        images,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleImagesProcessed = (dataUrls: string[]) => {
    setImages([...images, ...dataUrls]);
    setShowImageUploader(false);
  };

  const handleRemoveImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index));
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4">
        {note ? 'Edit Note' : 'New Note'}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
            Title
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter note title"
          />
        </div>

        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value as NoteCategory)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Content
          </label>
          <RichTextEditor
            content={content}
            onChange={setContent}
            placeholder="Write your note here..."
          />
        </div>

        {/* Image Upload Section */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">Images</label>
            <button
              type="button"
              onClick={() => setShowImageUploader(!showImageUploader)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              {showImageUploader ? 'Hide Uploader' : '+ Add Images'}
            </button>
          </div>

          {showImageUploader && (
            <div className="mb-4">
              <ImageUploader onImagesProcessed={handleImagesProcessed} maxImages={5} />
            </div>
          )}

          {images.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {images.map((img, index) => (
                <div key={index} className="relative group">
                  <img
                    src={img}
                    alt={`Upload ${index + 1}`}
                    className="w-full h-24 object-cover rounded border"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(index)}
                    className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-md transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
